import { StreamRoleId } from '../dto/mediaAvailability';
import { MediaSegmentsResponse } from '../dto/mediaDtos';
import { StructuredLogger } from '@mimir/primitive/structuredLogger';
import { bufferedUrlLimit, SourceChunkInfo } from './transferPipeline';
import { makeStaticUrlProvider } from '../io/urlProvider';
import { invariant } from '../util/invariant';

export interface MediaSegmentsUrlProvider {
  getMediaSegmentUrls(itemId: string, streamIds: StreamRoleId[], signedForMethod: 'get' | 'put', firstSegmentIndex: number): Promise<MediaSegmentsResponse>;
}
export function createSourceChunkInfoStreamForSegmentedMedia(
  urlProvider: MediaSegmentsUrlProvider,
  itemId: string,
  streamId: StreamRoleId,
  initSegmentIndex: number | null,
  startSegmentIndex: number,
  endSegmentIndex: number,
  structuredLogger: StructuredLogger,
  // Optionally embed a delimiter chunk at the segment boundaries
  segmentDelimiterChunk: Uint8Array | null,
): ReadableStream<SourceChunkInfo> {
  const ab = new AbortController();
  let index = startSegmentIndex;
  const initSegmentCount : 0 | 1 = (initSegmentIndex !== null) ? 1 : 0;
  const chunksPerSegment = 1 + ((segmentDelimiterChunk !== null) ? 1 : 0);
  const totalNumberOfChunks = ((endSegmentIndex - startSegmentIndex) * chunksPerSegment) + initSegmentCount;
  structuredLogger.log({ level: 'debug', message: 'createSourceChunkInfoStreamForSegmentedMedia', totalNumberOfChunks, endSegmentIndex, startSegmentIndex, initSegmentCount });
  return new ReadableStream<SourceChunkInfo>({
    async pull(controller) {
      if (initSegmentIndex !== null) {
        const segmentUrls = await urlProvider.getMediaSegmentUrls(itemId, [streamId], 'get', initSegmentIndex);
        const streamUrls = segmentUrls[streamId];
        const url = streamUrls[initSegmentIndex];
        controller.enqueue({
          resourceLocation: { type: 'http', provider: makeStaticUrlProvider(url) },
          totalNumberOfChunks,
          start: 0,
          end: null,
        });
        initSegmentIndex = null;
      }

      while (index < endSegmentIndex && (controller.desiredSize == null || controller.desiredSize > 0)) {
        structuredLogger.log({ message: 'Fetching URLs', index, level: 'debug' });
        const segmentUrls = await urlProvider.getMediaSegmentUrls(itemId, [streamId], 'get', index);
        const streamUrls = segmentUrls[streamId];
        invariant(streamUrls, 'Expected stream URLs for stream ID ' + streamId + ', only streams ' + Object.keys(segmentUrls).join(',') + ' are available');
        structuredLogger.log({ message: 'Fetched URLs', index, count: Object.keys(streamUrls).length, level: 'debug' });

        while (index < endSegmentIndex) {
          const url = streamUrls[index];
          if (!url) {
            break;
          }
          controller.enqueue({
            resourceLocation: { type: 'http', provider: makeStaticUrlProvider(url) },
            totalNumberOfChunks,
            start: 0,
            end: null,
          });
          if (segmentDelimiterChunk) {
            controller.enqueue({
              resourceLocation: { type: 'memory', bytes: segmentDelimiterChunk },
              totalNumberOfChunks,
              start: 0,
              end: null,
            });
          }
          ++index;
        }
      }
      structuredLogger.log({ message: 'URLs desiredSize', desiredSize: controller.desiredSize, level: 'debug' });
      if (index >= endSegmentIndex) {
        controller.close();
      }
    },
    cancel() {
      ab.abort();
    }
  }, new CountQueuingStrategy({ highWaterMark: bufferedUrlLimit }));
}


